import React, { type FC, type PropsWithChildren } from 'react';

import { useInView } from 'react-intersection-observer';
import { getColor, theme } from '@lucid/theme';

import type { Lucid } from '../../types/types';
import Hero from './hero';
import Box from '../box/box';
import Container from '../container/container';
import MediaCarousel from '../carousel/media-carousel';

const HeroCarousel: FC<PropsWithChildren<Lucid.Component.Hero.Carousel>> = ({
  className,
  heading,
  subheading = '',
  cta,
  headingColor,
  subheadingColor,
  heroColor,
  baseColor,
  carouselContentBackgroundColor = 'coolGrey.0',
  hasCarouselContentHeading = false,
  hasDarkBackground = false,
  hasLinkIconAnimation = false,
  hasWave,
  items = [],
  sliderTime = 7000,
  hasSSO,
  ariaLabel = '',
  ...rest
}) => {
  const carouselItems = items || [];
  const [inViewRef, isInView] = useInView({ threshold: 0.3 });
  const hasCarouselItems = carouselItems.length > 0;
  const waveColor = getColor({
    color: heroColor,
    theme: theme,
  });

  return (
    <Box className={className} css={theme => ({ backgroundColor: getColor({ color: baseColor, theme }) })} {...rest}>
      <Box
        css={theme => ({
          backgroundColor: getColor({
            color: heroColor,
            theme,
          }),
        })}
      >
        <Container>
          {heading && (
            <Hero
              css={[!hasWave && { maxWidth: '780px', margin: 'auto' }]}
              cta={cta}
              hasDarkBackground={hasDarkBackground}
              hasSSO={hasSSO}
              header={heading}
              headerVariant={hasWave ? 'hero4XL700' : 'hero2XL700'}
              headingColor={headingColor}
              iconAnimation={hasLinkIconAnimation ? 'navigational' : 'default'}
              isLeftAligned={hasWave}
              subheader={subheading}
              subheaderVariant={hasWave ? 'paragraphL400' : 'paragraphM400'}
              subheadingColor={subheadingColor}
            />
          )}
        </Container>
      </Box>
      {(hasWave || hasCarouselItems) && (
        <Box css={theme => ({ position: 'relative', zIndex: 1, paddingBottom: theme.space.s80 })}>
          {hasWave && (
            <Box
              css={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                aspectRatio: '1440 / 275',
                pointerEvents: 'none',
              }}
            >
              <svg
                fill="none"
                height="100%"
                preserveAspectRatio="none"
                style={{ display: 'block', width: '100%', height: '100%' }}
                viewBox="0 0 1440 275"
                width="100%"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M0,0 H1440 V50 H1440 V90 Q720,275 0,90 V50 H0 Z" fill={waveColor} />
              </svg>
            </Box>
          )}
          {hasCarouselItems && (
            <Container ref={inViewRef}>
              <MediaCarousel
                ariaLabel={ariaLabel}
                contentBackgroundColor={carouselContentBackgroundColor}
                hasLowerHeading={hasCarouselContentHeading}
                id="hero-carousel-items"
                isInView={isInView}
                items={carouselItems}
                sliderTime={sliderTime}
              />
            </Container>
          )}
        </Box>
      )}
    </Box>
  );
};

export default HeroCarousel;
